<template>
  <div class="ho-licence-panel">
    <div class="panel">
      <div class="panel-title">
        {{ $t('hit-admin.subscriptions.hoLicence.title') }}
      </div>
      <div class="panel-subtitle">
        {{ $t('hit-admin.subscriptions.hoLicence.subtitle') }}
      </div>

      <div class="toolbar">
        <hit-button
          :label="$t('hit-admin.subscriptions.hoLicence.refresh')"
          color="button"
          prefix-icon="refresh"
          :disabled="loading"
          @click="loadLicences"
        />
        <hit-button
          :label="$t('hit-admin.subscriptions.hoLicence.linkCompany')"
          color="accent"
          prefix-icon="add_link"
          :disabled="loading"
          @click="openLinkDialog"
        />
      </div>

      <div v-if="successMessage" class="alert alert-success">
        {{ successMessage }}
      </div>

      <div v-if="errorMessage" class="alert alert-error">
        {{ errorMessage }}
      </div>

      <div v-if="loading" class="loading-state">
        {{ $t('hit-admin.subscriptions.hoLicence.loading') }}
      </div>

      <div v-else-if="rows.length === 0" class="empty-state">
        {{ $t('hit-admin.subscriptions.hoLicence.noRows') }}
      </div>

      <template v-else>
        <div class="table-wrap">
          <table class="licence-table">
            <thead>
              <tr>
                <th>
                  {{
                    $t('hit-admin.subscriptions.hoLicence.columns.licenceId')
                  }}
                </th>
                <th class="col-num">
                  {{
                    $t('hit-admin.subscriptions.hoLicence.columns.userCount')
                  }}
                </th>
                <th class="col-bool">
                  {{ $t('hit-admin.subscriptions.hoLicence.columns.active') }}
                </th>
                <th class="col-num">
                  {{
                    $t('hit-admin.subscriptions.hoLicence.columns.moduleCount')
                  }}
                </th>
                <th class="col-num">
                  {{
                    $t('hit-admin.subscriptions.hoLicence.columns.companyCount')
                  }}
                </th>
                <th>
                  {{
                    $t('hit-admin.subscriptions.hoLicence.columns.mainCompany')
                  }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.id"
                class="licence-row"
                :class="{selected: selectedLicenceId === row.id}"
                @click="selectLicence(row.id)"
              >
                <td class="col-id">{{ row.id }}</td>
                <td class="col-num">{{ formatUserCount(row.user_count) }}</td>
                <td class="col-bool">
                  <span
                    class="pill"
                    :class="row.active ? 'pill-ok' : 'pill-muted'"
                  >
                    {{
                      row.active
                        ? $t('hit-admin.subscriptions.hoLicence.activeYes')
                        : $t('hit-admin.subscriptions.hoLicence.activeNo')
                    }}
                  </span>
                </td>
                <td class="col-num">{{ row.modules?.length || 0 }}</td>
                <td class="col-num">{{ row.companies?.length || 0 }}</td>
                <td>{{ getMainCompanyLabel(row) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="selectedLicence" class="detail-panel">
          <div class="detail-section">
            <div class="detail-title">
              {{ $t('hit-admin.subscriptions.hoLicence.detailModules') }}
            </div>
            <div v-if="!selectedLicence.modules?.length" class="detail-empty">
              {{ $t('hit-admin.subscriptions.hoLicence.noModules') }}
            </div>
            <div v-else class="table-wrap">
              <table class="detail-table">
                <thead>
                  <tr>
                    <th>
                      {{
                        $t('hit-admin.subscriptions.hoLicence.columns.module')
                      }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="module in selectedLicence.modules"
                    :key="module.id"
                  >
                    <td>{{ formatModuleLabel(module) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="detail-section">
            <div class="detail-title">
              {{ $t('hit-admin.subscriptions.hoLicence.detailCompanies') }}
            </div>
            <div v-if="!selectedLicence.companies?.length" class="detail-empty">
              {{ $t('hit-admin.subscriptions.hoLicence.noCompanies') }}
            </div>
            <div v-else class="table-wrap">
              <table class="detail-table">
                <thead>
                  <tr>
                    <th>
                      {{
                        $t('hit-admin.subscriptions.hoLicence.columns.company')
                      }}
                    </th>
                    <th>
                      {{
                        $t(
                          'hit-admin.subscriptions.hoLicence.columns.companyId'
                        )
                      }}
                    </th>
                    <th class="col-bool" />
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="company in selectedLicence.companies"
                    :key="company.id"
                  >
                    <td>{{ company.designation || '—' }}</td>
                    <td class="col-id">{{ company.company_id }}</td>
                    <td class="col-bool">
                      <span v-if="company.main" class="pill pill-main">
                        {{ $t('hit-admin.subscriptions.hoLicence.mainBadge') }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div v-else class="empty-state detail-hint">
          {{ $t('hit-admin.subscriptions.hoLicence.selectLicenceHint') }}
        </div>
      </template>
    </div>

    <hit-dialog
      :visible="linkDialogVisible"
      :title="$t('hit-admin.subscriptions.hoLicence.linkTitle')"
      size="large"
      :show-ok-button="false"
      :show-cancel-button="false"
      @update:visible="onLinkDialogVisibleChange"
    >
      <template #body>
        <div class="form-grid">
          <hit-select
            class="w-full"
            :label="$t('hit-admin.subscriptions.hoLicence.linkLicence')"
            :options="licenceOptions"
            :value="linkForm.licence_id"
            item-id-property="id"
            :label-fields="['label']"
            filterable
            clearable="false"
            @change="onLinkLicenceChange"
          />

          <hit-select
            class="w-full"
            :label="$t('hit-admin.subscriptions.hoLicence.linkCompanyField')"
            :options="companyOptions"
            :value="linkForm.company_id"
            item-id-property="id"
            :label-fields="['label']"
            filterable
            clearable="false"
            @change="onLinkCompanyChange"
          />

          <hit-input-checkbox
            class="w-full"
            :values="$t('hit-admin.subscriptions.hoLicence.setAsMain')"
            :value="linkForm.set_as_main"
            @change="onSetAsMainChange"
          />
        </div>
        <div v-if="linkFormError" class="alert alert-error mt-3">
          {{ linkFormError }}
        </div>
      </template>
      <template #footer>
        <hit-button
          :label="$t('hit-admin.subscriptions.hoLicence.cancel')"
          color="button"
          :disabled="linking"
          @click="closeLinkDialog"
        />
        <hit-button
          :label="$t('hit-admin.subscriptions.hoLicence.save')"
          color="accent"
          prefix-icon="save"
          :disabled="linking"
          @click="saveLink"
        />
      </template>
    </hit-dialog>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {
  HitButton,
  HitDialog,
  HitInputCheckbox,
  HitSelect,
} from '@hit/components';
import {adminApi, COMPANY_OPTIONS_MAX_LIMIT} from '@admin/services/adminApi';

function emptyLinkForm() {
  return {
    licence_id: null,
    company_id: null,
    set_as_main: false,
  };
}

export default defineComponent({
  name: 'HoLicencePanel',
  components: {
    HitButton,
    HitDialog,
    HitInputCheckbox,
    HitSelect,
  },
  data() {
    return {
      rows: [],
      loading: false,
      errorMessage: '',
      successMessage: '',
      selectedLicenceId: null,
      companyOptions: [],
      linkDialogVisible: false,
      linkForm: emptyLinkForm(),
      linkFormError: '',
      linking: false,
    };
  },
  computed: {
    selectedLicence() {
      if (!this.selectedLicenceId) return null;
      return this.rows.find((row) => row.id === this.selectedLicenceId) || null;
    },
    licenceOptions() {
      return this.rows.map((row) => ({
        id: row.id,
        label: `${row.id} (${this.formatUserCount(row.user_count)} util.)`,
      }));
    },
  },
  async mounted() {
    await Promise.all([this.loadLicences(), this.loadCompanyOptions()]);
  },
  methods: {
    async loadCompanyOptions() {
      try {
        const res = await adminApi.listCompanyOptions({
          limit: COMPANY_OPTIONS_MAX_LIMIT,
        });
        this.companyOptions = (res?.rows || []).map((company) => ({
          id: company.id,
          label:
            [company.designation, company.vat_no].filter(Boolean).join(' — ') ||
            company.id,
        }));
      } catch {
        this.companyOptions = [];
      }
    },
    async loadLicences() {
      this.loading = true;
      this.errorMessage = '';
      this.successMessage = '';
      const previousSelection = this.selectedLicenceId;
      try {
        const list = await adminApi.listLicences();
        const details = await Promise.all(
          (list || []).map((licence) => adminApi.getLicenceDetails(licence.id))
        );
        this.rows = details;
        if (
          previousSelection &&
          this.rows.some((row) => row.id === previousSelection)
        ) {
          this.selectedLicenceId = previousSelection;
        } else if (this.rows.length === 1) {
          this.selectedLicenceId = this.rows[0].id;
        } else if (
          previousSelection &&
          !this.rows.some((row) => row.id === previousSelection)
        ) {
          this.selectedLicenceId = null;
        }
      } catch (e) {
        this.rows = [];
        this.selectedLicenceId = null;
        this.errorMessage =
          e?.message || this.$t('hit-admin.subscriptions.hoLicence.loadError');
      } finally {
        this.loading = false;
      }
    },
    selectLicence(licenceId) {
      this.selectedLicenceId = licenceId;
    },
    formatUserCount(value) {
      if (value === null || value === undefined) return '—';
      return String(value);
    },
    formatModuleLabel(module) {
      return module.designation_key || module.module_id || '—';
    },
    getMainCompanyLabel(row) {
      const mainCompany = (row.companies || []).find((company) => company.main);
      if (!mainCompany) return '—';
      return mainCompany.designation || mainCompany.company_id;
    },
    openLinkDialog() {
      this.linkForm = emptyLinkForm();
      if (this.selectedLicenceId) {
        this.linkForm.licence_id = this.selectedLicenceId;
      }
      this.linkFormError = '';
      this.linkDialogVisible = true;
    },
    closeLinkDialog() {
      this.linkDialogVisible = false;
      this.linkFormError = '';
    },
    onLinkDialogVisibleChange(visible) {
      if (!visible) {
        this.closeLinkDialog();
      }
    },
    onLinkLicenceChange(value) {
      this.linkForm.licence_id = value;
    },
    onLinkCompanyChange(value) {
      this.linkForm.company_id = value;
    },
    onSetAsMainChange(value) {
      this.linkForm.set_as_main = !!value;
    },
    validateLinkForm() {
      if (!this.linkForm.licence_id) {
        return this.$t(
          'hit-admin.subscriptions.hoLicence.errors.licenceRequired'
        );
      }
      if (!this.linkForm.company_id) {
        return this.$t(
          'hit-admin.subscriptions.hoLicence.errors.companyRequired'
        );
      }
      return '';
    },
    async saveLink() {
      this.linkFormError = this.validateLinkForm();
      if (this.linkFormError) return;

      this.linking = true;
      try {
        const payload = {
          company_ids: [this.linkForm.company_id],
        };
        if (this.linkForm.set_as_main) {
          payload.main_company_id = this.linkForm.company_id;
        }
        await adminApi.addCompaniesToLicence(this.linkForm.licence_id, payload);
        this.selectedLicenceId = this.linkForm.licence_id;
        this.closeLinkDialog();
        this.successMessage = this.$t(
          'hit-admin.subscriptions.hoLicence.linkSuccess'
        );
        await this.loadLicences();
      } catch (e) {
        this.linkFormError = e?.message || String(e);
      } finally {
        this.linking = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.ho-licence-panel {
  height: 100%;
}

.panel {
  border-radius: 1rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(6px);
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.04),
    0 12px 30px rgba(16, 24, 40, 0.08);
  padding: 1.1rem 1.15rem 1.25rem;
}

.panel-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: rgba(15, 23, 42, 0.95);
}

.panel-subtitle {
  margin-top: 0.25rem;
  color: rgba(100, 116, 139, 1);
  font-size: 0.9rem;
  line-height: 1.35rem;
}

.toolbar {
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.loading-state,
.empty-state {
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 0.75rem;
  background: rgba(248, 250, 252, 1);
  color: rgba(100, 116, 139, 1);
  font-size: 0.9rem;
}

.detail-hint {
  margin-top: 0.75rem;
}

.alert {
  margin-top: 0.75rem;
  padding: 0.65rem 0.75rem;
  border-radius: 0.65rem;
  font-size: 0.85rem;
}

.alert-error {
  background: rgba(254, 242, 242, 1);
  border: 1px solid rgba(248, 113, 113, 0.35);
  color: #b91c1c;
}

.alert-success {
  background: rgba(220, 252, 231, 1);
  border: 1px solid rgba(74, 222, 128, 0.35);
  color: #166534;
}

.table-wrap {
  margin-top: 1rem;
  overflow: auto;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.75rem;
}

.licence-table,
.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  th,
  td {
    padding: 0.55rem 0.65rem;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
    text-align: left;
    vertical-align: middle;
  }

  th {
    background: rgba(248, 250, 252, 1);
    font-weight: 600;
    color: rgba(51, 65, 85, 1);
    white-space: nowrap;
  }
}

.licence-row {
  cursor: pointer;

  &:hover {
    background: rgba(248, 250, 252, 0.65);
  }

  &.selected {
    background: rgba(239, 246, 255, 0.9);
  }
}

.col-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', 'Courier New', monospace;
  font-size: 0.8rem;
  word-break: break-all;
}

.col-num {
  white-space: nowrap;
  text-align: right;
}

.col-bool {
  white-space: nowrap;
  text-align: center;
}

.pill {
  display: inline-block;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.pill-ok {
  background: rgba(220, 252, 231, 1);
  color: #166534;
}

.pill-main {
  background: rgba(219, 234, 254, 1);
  color: #1d4ed8;
}

.pill-muted {
  background: rgba(241, 245, 249, 1);
  color: rgba(100, 116, 139, 1);
}

.detail-panel {
  margin-top: 1rem;
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
}

.detail-section {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.75rem;
  padding: 0.85rem;
  background: rgba(248, 250, 252, 0.45);
}

.detail-title {
  font-weight: 600;
  color: rgba(15, 23, 42, 0.9);
  margin-bottom: 0.5rem;
}

.detail-empty {
  color: rgba(100, 116, 139, 1);
  font-size: 0.875rem;
  padding: 0.5rem 0;
}

.detail-section .table-wrap {
  margin-top: 0.5rem;
}

.form-grid {
  display: grid;
  gap: 0.75rem;
}
</style>
